/**
 * PATH: apps/qa/src/pages/area-execution/ItemChangeActions.tsx
 * FILE_ID: QA-HUB-ITEM-CHANGE-ACTIONS-001
 * PURPOSE: Hub add/edit/delete + pending request controls for one checklist row.
 */
import { useState } from 'react'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { QaItemChangeBanner } from '@quotech/ui'
import type { ItemInstance } from '@/types/qa'
import {
  addAreaItem,
  approveItemChange,
  deleteAreaItem,
  raiseItemChange,
  rejectItemChange,
  updateTemplateItem,
} from './area-execution-api'

export function ItemChangeActions({
  item,
  areaId,
  disabled = false,
  showEditActions = true,
  onMutated,
}: {
  item: ItemInstance
  areaId: string
  disabled?: boolean
  showEditActions?: boolean
  onMutated: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(false)
  const [title, setTitle] = useState(item.title || '')
  const canApprove = Boolean(item.can_verify_completion)
  const pending = item.pending_change_id
    ? { id: item.pending_change_id, kind: item.pending_change_kind ?? 'edit' }
    : null

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      onMutated()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mt-2 space-y-1.5">
      {pending && (
        <QaItemChangeBanner
          kind={pending.kind}
          canApprove={canApprove}
          busy={busy}
          onApprove={() => run(() => approveItemChange(pending.id))}
          onReject={() => run(() => rejectItemChange(pending.id))}
        />
      )}
      {item.revised && !pending && (
        <p className="text-[11px] text-muted-foreground">Revised after issue.</p>
      )}
      {showEditActions && !disabled && !pending && (
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => addAreaItem(areaId, {}, item.item_number))}
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-2 py-1 text-[11px] text-muted-foreground"
          >
            <Plus className="h-3 w-3" aria-hidden /> Add below
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setEditing((open) => !open)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground"
          >
            <Pencil className="h-3 w-3" aria-hidden /> Edit
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void run(() => deleteAreaItem(item.id))}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] text-muted-foreground"
          >
            <Trash2 className="h-3 w-3" aria-hidden /> Delete
          </button>
        </div>
      )}
      {editing && (
        <div className="flex flex-wrap items-center gap-1.5">
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            aria-label={`Edit title for ${item.item_number || 'item'}`}
            className="min-h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-xs"
          />
          <button
            type="button"
            disabled={busy || !title.trim()}
            onClick={() => void run(async () => {
              if (canApprove && item.template_item) {
                await updateTemplateItem(item.template_item, { title })
              } else {
                await raiseItemChange(item.id, 'edit', { title })
              }
              setEditing(false)
            })}
            className="rounded-md bg-primary px-2 py-1 text-[11px] text-primary-foreground"
          >
            {canApprove ? 'Save' : 'Request'}
          </button>
        </div>
      )}
    </div>
  )
}
